/* VoiceAssistant — reusable voice-dictation UI (Fase 1 of the
   approved voice-assistant plan, 2026-10-26).

   Opens as a DraggableModal, same window convention as the rest of
   this app. Records audio with MediaRecorder (works on iOS, unlike
   the free browser speech-recognition API — see voice-transcribe.js's
   header comment for why that's required here), sends it to
   /.netlify/functions/voice-transcribe, then sends the transcript +
   the running conversation to /.netlify/functions/voice-turn, which
   returns the next question and any captured/corrected fields. Reads
   each question back out loud with the browser's own text-to-speech
   (`speechSynthesis` — free, built in, works on iOS).

   This component NEVER saves anything by itself. When the engine
   says the capture is complete, it reads a full spoken summary and
   waits for an explicit "sí"/"correcto" before calling `onComplete`
   with the captured state — the caller (ShipmentRegistry.js) is the
   one that decides what to do with it (pre-fill the real form for
   Arturo to review and save through the normal, unmodified flow).
   Saying "no" at the confirmation step reopens the conversation
   instead of closing — Arturo can correct whatever was wrong. */

import { useState, useRef, useCallback, useEffect } from "react";
import DraggableModal from "./DraggableModal";

var T = {
  en: {
    title: "Voice dictation",
    tapToTalk: "Tap the microphone and speak",
    listening: "Listening…",
    thinking: "Thinking…",
    confirmPrompt: "Say \"yes\" to confirm, or \"no\" to correct something.",
    yesWords: ["yes", "correct", "confirm", "that's right", "save it"],
    noWords: ["no", "wrong", "incorrect", "change"],
    micError: "Couldn't access the microphone. Check your browser's permission for this site.",
    serverError: "Something went wrong — try again.",
    close: "Close",
    startOver: "Start over",
  },
  es: {
    title: "Dictado por voz",
    tapToTalk: "Toca el micrófono y habla",
    listening: "Escuchando…",
    thinking: "Pensando…",
    confirmPrompt: "Di \"sí\" para confirmar, o \"no\" si algo está mal.",
    yesWords: ["sí", "si", "correcto", "confirmo", "así es", "guárdalo", "guardalo"],
    noWords: ["no", "mal", "incorrecto", "cambia", "cambiar"],
    micError: "No se pudo acceder al micrófono. Revisa el permiso del navegador para este sitio.",
    serverError: "Algo salió mal — intenta de nuevo.",
    close: "Cerrar",
    startOver: "Empezar de nuevo",
  },
};

function speak(text, lang) {
  try {
    if (!window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    var u = new window.SpeechSynthesisUtterance(text);
    u.lang = lang === "en" ? "en-US" : "es-MX";
    window.speechSynthesis.speak(u);
  } catch (e) { /* speech synthesis is a nicety, never block on it */ }
}

function pickMimeType() {
  var candidates = ["audio/mp4", "audio/webm", "audio/webm;codecs=opus"];
  for (var i = 0; i < candidates.length; i++) {
    if (window.MediaRecorder && window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(candidates[i])) return candidates[i];
  }
  return "";
}

function blobToBase64(blob) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onloadend = function () {
      var result = String(reader.result || "");
      var comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function matchesAny(text, words) {
  var low = (text || "").toLowerCase();
  return words.some(function (w) { return low.indexOf(w) >= 0; });
}

export default function VoiceAssistant({ contract, lang, onComplete, onClose }) {
  var t = T[lang === "en" ? "en" : "es"];
  var [state, setState] = useState({});
  var [history, setHistory] = useState([]);
  var [awaitingConfirm, setAwaitingConfirm] = useState(false);
  var [summary, setSummary] = useState("");
  var [recording, setRecording] = useState(false);
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState("");
  var mediaRecorderRef = useRef(null);
  var chunksRef = useRef([]);
  var startedRef = useRef(false);

  var askFirstQuestion = useCallback(function () {
    var first = (contract.fields || [])[0];
    var q = first ? (lang === "en" ? "Let's get started. " : "Empecemos. ") + first.label + (lang === "en" ? "?" : "?") : "";
    setHistory([{ role: "assistant", text: q }]);
    speak(q, lang);
  }, [contract, lang]);

  useEffect(function () {
    if (startedRef.current) return;
    startedRef.current = true;
    askFirstQuestion();
    return function () { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {} };
  }, [askFirstQuestion]);

  var startRecording = async function () {
    setError("");
    try {
      var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      var mimeType = pickMimeType();
      var rec = new window.MediaRecorder(stream, mimeType ? { mimeType: mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = function (e) { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = function () {
        stream.getTracks().forEach(function (tr) { tr.stop(); });
        handleRecordingStopped(mimeType || "audio/webm");
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setError(t.micError);
    }
  };

  var stopRecording = function () {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  var handleRecordingStopped = async function (mimeType) {
    setBusy(true);
    setError("");
    try {
      var blob = new Blob(chunksRef.current, { type: mimeType });
      var base64 = await blobToBase64(blob);
      var resp = await fetch("/.netlify/functions/voice-transcribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audioBase64: base64, mimeType: mimeType }),
      });
      var data = await resp.json();
      if (!data.success) { setError(data.error || t.serverError); setBusy(false); return; }
      var transcript = (data.text || "").trim();
      if (!transcript) { setBusy(false); return; }

      if (awaitingConfirm) {
        await handleConfirmAnswer(transcript);
        setBusy(false);
        return;
      }

      var newHistory = history.concat([{ role: "user", text: transcript }]);
      setHistory(newHistory);
      var turnResp = await fetch("/.netlify/functions/voice-turn", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract: contract, state: state, history: newHistory, transcript: transcript }),
      });
      var turnData = await turnResp.json();
      if (!turnData.success) { setError(turnData.error || t.serverError); setBusy(false); return; }

      var nextState = Object.assign({}, state, turnData.updates || {});
      setState(nextState);

      if (turnData.isComplete) {
        setAwaitingConfirm(true);
        setSummary(turnData.summary || "");
        var confirmText = (turnData.summary || "") + " " + t.confirmPrompt;
        speak(confirmText, lang);
      } else {
        setHistory(newHistory.concat([{ role: "assistant", text: turnData.nextQuestion || "" }]));
        speak(turnData.nextQuestion || "", lang);
      }
    } catch (e) {
      setError(t.serverError);
    } finally {
      setBusy(false);
    }
  };

  var handleConfirmAnswer = async function (transcript) {
    if (matchesAny(transcript, t.yesWords)) {
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
      onComplete(state);
      return;
    }
    if (matchesAny(transcript, t.noWords)) {
      /* Reopen the conversation so Arturo can say what to fix — the
         engine already has the full state, so the next dictated
         sentence ("el precio son 24, no 23") goes right back through
         the same correction path as any mid-conversation fix. */
      setAwaitingConfirm(false);
      var reopenQ = lang === "en" ? "Okay, what should I change?" : "Está bien, ¿qué quieres cambiar?";
      setHistory(history.concat([{ role: "assistant", text: reopenQ }]));
      speak(reopenQ, lang);
      return;
    }
    /* Didn't clearly hear yes/no — ask again rather than guessing on
       something this consequential. */
    speak(t.confirmPrompt, lang);
  };

  return (
    <DraggableModal title={"🎙️ " + t.title} onClose={onClose} initialWidth={480} initialHeight={520}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", padding: "24px 20px", gap: 18 }}>
        <div style={{ flex: 1, width: "100%", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
          {history.map(function (h, i) {
            return (
              <div key={i} style={{ alignSelf: h.role === "assistant" ? "flex-start" : "flex-end", maxWidth: "85%", padding: "8px 12px", borderRadius: 10, background: h.role === "assistant" ? "var(--color-bg)" : "var(--color-accent-bg)", color: h.role === "assistant" ? "var(--color-text)" : "var(--color-accent)" }}>
                {h.text}
              </div>
            );
          })}
          {awaitingConfirm && summary && (
            <div style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.3)", color: "#B45309", fontWeight: 600 }}>
              {summary}
            </div>
          )}
        </div>

        {error && <div style={{ color: "var(--color-danger)", fontSize: 12, textAlign: "center" }}>{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={busy}
            style={{
              width: 72, height: 72, borderRadius: "50%", border: "none", cursor: busy ? "not-allowed" : "pointer",
              background: recording ? "#DC2626" : "var(--color-accent)", color: "#fff", fontSize: 28,
              boxShadow: recording ? "0 0 0 8px rgba(220,38,38,0.15)" : "0 2px 8px rgba(0,0,0,0.2)",
              transition: "box-shadow 0.2s",
            }}
          >
            🎙️
          </button>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", textAlign: "center" }}>
            {busy ? t.thinking : (recording ? t.listening : t.tapToTalk)}
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}
